import { AfterViewChecked, AfterViewInit, Component, HostListener, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { CommunityPresenceService, CommunityPresenceState } from 'src/app/core/services/community-presence.service';
import { PubNubService } from 'src/app/core/services/pubnub.service';
import { CommunityProfilesService } from 'src/app/core/services/community-profiles.service';
import { CommunityMaincontentComponent } from '../../community/community-maincontent/community-maincontent.component';
import {
  isGroupedWithPrevious, PanelMember, parseTip, spaceAvatar, splitByPresence, TipView, typingLabel,
} from '../community-format';
import { ChatMembers } from './cm-chat-members';
import { CommunityLayoutService } from '../community-layout.service';
import { ReactionHover } from '../cm-message/cm-message.component';

const WIDE_MIN_PX = 1180;

/**
 * v2 chat area. Inherits every data, PubNub, poll, tip, report, thread, file and
 * membership handler from CommunityMaincontentComponent; only the template changes.
 */
@Component({
  selector: 'app-cm-chat',
  templateUrl: './cm-chat.component.html',
  styleUrls: ['./cm-chat.component.scss'],
})
export class CmChatComponent extends CommunityMaincontentComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly layout = inject(CommunityLayoutService);
  private readonly presence = inject(CommunityPresenceService);
  private readonly subscribeService = inject(CommanSubscribeService);
  private presenceSub?: Subscription;
  private presenceChannel = '';
  private panelRestored = false;
  presenceState: CommunityPresenceState | null = null;
  /** Latest PubNub channel members (the inherited code fetches them on every space load). */
  private pubnubMembers: any = null;
  private readonly membersSub = inject(PubNubService).channelMembers$.subscribe(m => (this.pubnubMembers = m));
  private readonly members = new ChatMembers(inject(CommunityProfilesService));
  private onlineCache: { source: Set<string> | null; ids: Set<string> | null } | null = null;
  isNarrow = window.innerWidth < WIDE_MIN_PX;

  get signedIn(): boolean {
    return !!this.userInfo?.id;
  }

  /** Same rule as the classic menuActions (creator does not get Join / Star / Report). */
  get isCreator(): boolean {
    return !(this.channelDetails?.createdBy?.split('_')[1] !== this.userInfo?.id);
  }

  get panelOpen(): boolean {
    return !!this.sidenav?.opened;
  }

  get spaceAvatarUrl(): string | null {
    const groups = this.subscribeService.perfomerInfoData?.data?.Data?.[0]?.groups;
    return spaceAvatar(this.channelDetails, groups);
  }

  /** Members of this space only (see ChatMembers). */
  get panelMembers(): PanelMember[] {
    const self = this.signedIn
      ? { userId: this.currentUserId, nickname: this.userInfo.user_display_name, profileUrl: this.userInfo.profile_pic }
      : null;
    return this.members.list(this.channelDetails?.channelVisibility === 'private', this.filteredTeamMembers,
      this.pubnubMembers, this.messagesList, self);
  }

  /** Online members of this space (header count). */
  get onlineMemberCount(): number | null {
    return this.onlineIds ? splitByPresence(this.panelMembers, this.onlineIds).online.length : null;
  }

  get memberTotal(): number {
    return Math.max(Number(this.channelDetails?.memberCount) || 0, this.panelMembers.length);
  }

  /** null = presence off or not known yet. Viewers always count themselves as online. */
  get onlineIds(): Set<string> | null {
    const online = this.presence.enabled ? this.presenceState?.online ?? null : null;
    if (this.onlineCache?.source !== online) {
      const ids = online && this.signedIn ? new Set([...online, this.currentUserId]) : online;
      this.onlineCache = { source: online, ids };
    }
    return this.onlineCache.ids;
  }

  get typingText(): string {
    return typingLabel(this.presenceState?.typing || []);
  }

  tipOf(message: any): TipView | null {
    return message && !message.isBlocked && message.type !== 'poll' ? parseTip(message.message) : null;
  }

  isGrouped(index: number): boolean {
    return isGroupedWithPrevious(this.messagesList, index, !!this.messagesList[index]?.timestamp && this.displayDate(index));
  }

  timeOf(message: any): string {
    return message?.msgTime && message.msgTime !== 'Invalid Date' ? message.msgTime : '';
  }

  threadTimeOf(message: any): string {
    const day = message?.timestamp && message.timestamp !== 'Invalid Date' ? this.getFormattedDate(message.timestamp) : '';
    return [day, this.timeOf(message)].filter(Boolean).join(' ');
  }

  trackMessage(_: number, m: any): string {
    return m?.messageId;
  }

  onReactionEnter(e: ReactionHover, message: any): void {
    this.showReactionUsers(e.emoji, message, e.trigger);
  }

  vote(message: any, optionId: string): void {
    if (!this.signedIn) return;
    this.onVoteOptionClick(message, optionId, null as any);
  }

  /** Header button: open Details (or close the panel) and remember the choice. */
  togglePanel(): void {
    if (this.panelOpen && !this.threadMessage) {
      this.closeSidenav();
      this.layout.setSidePanelPref(false);
    } else {
      this.threadMessage = null;
      this.openSidenav();
      this.layout.setSidePanelPref(true);
    }
  }

  /** Back to the panel's Details or Threads list from an open thread. */
  leaveThread(): void {
    this.threadMessage = null;
    this.threadChannelUrl = '';
  }

  closePanel(): void {
    this.closeSidenav();
    this.layout.setSidePanelPref(false);
  }

  showSpaces(): void {
    this.layout.setRailOpen(true);
  }

  onTyping(): void {
    this.presence.notifyTyping(this.userInfo?.user_display_name);
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.syncPanelAndPresence();
  }

  ngAfterViewChecked(): void {
    this.syncPanelAndPresence();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isNarrow = window.innerWidth < WIDE_MIN_PX;
  }

  /**
   * Once the space has loaded: restore the remembered panel state on wide screens
   * (without the "View details" analytics event, since the viewer did not click),
   * and start presence for this space's channel when the flag is on.
   */
  private syncPanelAndPresence(): void {
    if (!this.isChannelAvailable || !this.sidenav) return;
    if (!this.panelRestored) {
      this.panelRestored = true;
      const pref = this.layout.getSidePanelPref();
      if (!this.isNarrow && pref !== false && !this.sidenav.opened) {
        setTimeout(() => this.sidenav.open());
      }
    }
    if (this.presence.enabled && this.channelUrl && this.channelUrl !== this.presenceChannel) {
      this.presenceChannel = this.channelUrl;
      this.presenceSub?.unsubscribe();
      this.presenceSub = this.presence.watch(this.channelUrl, this.currentUserId).subscribe(s => (this.presenceState = s));
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.membersSub.unsubscribe();
    this.presenceSub?.unsubscribe();
    this.presence.stop();
  }
}
